import React, { useMemo, useState } from "react";
import styled from "styled-components";
import FormStepEditor from "./FormStepEditor";
import FormFieldTypePicker from "./FormFieldTypePicker";
import FormFieldEditor from "./FormFieldEditor";
import { FIELD_TYPE_OPTIONS } from "../../../constants";

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  gap: 16px;

  @media (max-width: 900px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const Title = styled.h3`
  margin: 0;
  font-size: 20px;
  color: #1a1a1a;
`;

const Button = styled.button`
  border: 1px solid #0073ab;
  background: ${({ secondary }) => (secondary ? "#ffffff" : "#0073ab")};
  color: ${({ secondary }) => (secondary ? "#0073ab" : "#ffffff")};
  border-radius: 6px;
  padding: 10px 14px;
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  opacity: ${({ disabled }) => (disabled ? 0.55 : 1)};
`;

const FieldsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FieldRow = styled.div`
  display: grid;
  grid-template-columns: auto 1fr auto auto;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  border-radius: 8px;
  border: 1px solid #e6ecf1;
  background: #ffffff;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const TypeBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 5px 10px;
  border-radius: 999px;
  background: #eef6fb;
  color: #0073ab;
  font-size: 12px;
  font-weight: 700;
`;

const FieldText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const FieldName = styled.span`
  font-weight: 700;
  color: #1a1a1a;
`;

const FieldMeta = styled.span`
  color: #707070;
  font-size: 12px;
`;

const EmptyStepText = styled.p`
  color: #707070;
  margin: 16px 0 0 0;
`;

function createStepId(index) {
  return `etapa-${index + 1}`;
}

function createField(type) {
  return {
    id: `${type}-campo`,
    type,
    label: "",
    placeholder: "",
    helpText: "",
    required: false,
    options: ["checkbox", "radio", "select"].includes(type) ? [] : undefined,
    min: type === "slider" ? "0" : "",
    max: type === "slider" ? "10" : "",
    step: type === "slider" ? "1" : "",
    valueType: type === "slider" ? "integer" : "",
    imageUrl: type === "image_click" ? "" : "",
    minLength: "",
    maxLength: "",
    pattern: "",
    errorMessage: "",
    maskType: ["text", "textarea"].includes(type) ? "none" : "",
    defaultValue: "",
  };
}

function findFieldPosition(steps, fieldType) {
  for (let stepIndex = 0; stepIndex < steps.length; stepIndex += 1) {
    const fieldIndex = steps[stepIndex].fields.findIndex((field) => field.type === fieldType);
    if (fieldIndex >= 0) {
      return { stepIndex, fieldIndex };
    }
  }

  return null;
}

function FormBuilderStep({ formValues, setFormValues }) {
  const [pickerStepIndex, setPickerStepIndex] = useState(null);
  const [activeEditor, setActiveEditor] = useState(null);

  const summaryPosition = useMemo(
    () => findFieldPosition(formValues.steps, "summary"),
    [formValues.steps]
  );
  const thermalPosition = useMemo(
    () => findFieldPosition(formValues.steps, "thermal_capture"),
    [formValues.steps]
  );

  const updateSteps = (steps) => {
    setFormValues({ ...formValues, steps });
  };

  const handleAddStep = () => {
    const nextSteps = [
      ...formValues.steps,
      {
        id: createStepId(formValues.steps.length),
        title: "",
        description: "",
        fields: [],
      },
    ];

    updateSteps(nextSteps);
  };

  const handleStepChange = (stepIndex, nextStep) => {
    const nextSteps = [...formValues.steps];
    nextSteps[stepIndex] = nextStep;
    updateSteps(nextSteps);
  };

  const handleDeleteStep = (stepIndex) => {
    const nextSteps = formValues.steps.filter((_, index) => index !== stepIndex);
    updateSteps(nextSteps.map((step, index) => ({ ...step, id: step.id || createStepId(index) })));
    setActiveEditor(null);
    setPickerStepIndex(null);
  };

  const handleMoveStep = (fromIndex, toIndex) => {
    if (toIndex < 0 || toIndex >= formValues.steps.length) {
      return;
    }

    const nextSteps = [...formValues.steps];
    const [removedStep] = nextSteps.splice(fromIndex, 1);
    nextSteps.splice(toIndex, 0, removedStep);
    updateSteps(nextSteps);
  };

  const handleSelectFieldType = (stepIndex, type) => {
    const nextSteps = [...formValues.steps];
    nextSteps[stepIndex] = {
      ...nextSteps[stepIndex],
      fields: [...nextSteps[stepIndex].fields, createField(type)],
    };
    updateSteps(nextSteps);
    setPickerStepIndex(null);
    setActiveEditor({
      stepIndex,
      fieldIndex: nextSteps[stepIndex].fields.length - 1,
    });
  };

  const handleFieldChange = (stepIndex, fieldIndex, nextField) => {
    const nextSteps = [...formValues.steps];
    const nextFields = [...nextSteps[stepIndex].fields];
    nextFields[fieldIndex] = nextField;
    nextSteps[stepIndex] = {
      ...nextSteps[stepIndex],
      fields: nextFields,
    };
    updateSteps(nextSteps);
  };

  const handleDeleteField = (stepIndex, fieldIndex) => {
    const nextSteps = [...formValues.steps];
    nextSteps[stepIndex] = {
      ...nextSteps[stepIndex],
      fields: nextSteps[stepIndex].fields.filter((_, index) => index !== fieldIndex),
    };
    updateSteps(nextSteps);
    setActiveEditor(null);
  };

  const disabledTypes = {};

  if (summaryPosition) {
    disabledTypes.summary = "Já existe um campo de resumo neste formulário";
  }

  if (thermalPosition) {
    disabledTypes.thermal_capture = "Já existe um campo de captura térmica neste formulário";
  }

  const canAddStep = !summaryPosition;

  const canAddFieldToStep = (stepIndex) => {
    if (!summaryPosition) {
      return true;
    }

    return stepIndex < summaryPosition.stepIndex;
  };

  return (
    <>
      <Header>
        <Title>Estrutura do formulário</Title>
        <Button
          onClick={handleAddStep}
          disabled={!canAddStep}
          title={
            canAddStep
              ? ""
              : "Remova ou mova o campo de resumo para adicionar novas etapas"
          }
        >
          Adicionar etapa
        </Button>
      </Header>

      {formValues.steps.map((step, stepIndex) => {
        const allowAddField = canAddFieldToStep(stepIndex);

        return (
          <FormStepEditor
            key={`${step.id}-${stepIndex}`}
            step={step}
            index={stepIndex}
            onChange={(nextStep) => handleStepChange(stepIndex, nextStep)}
            onMoveUp={() => handleMoveStep(stepIndex, stepIndex - 1)}
            onMoveDown={() => handleMoveStep(stepIndex, stepIndex + 1)}
            onDelete={() => handleDeleteStep(stepIndex)}
            onAddField={() => {
              if (!allowAddField) {
                return;
              }

              setPickerStepIndex(stepIndex);
              setActiveEditor(null);
            }}
            addFieldDisabled={!allowAddField}
            addFieldTooltip={
              allowAddField
                ? ""
                : "Não é possível adicionar campos após um campo de resumo"
            }
          >
            {pickerStepIndex === stepIndex ? (
              <FormFieldTypePicker
                onSelectType={(type) => handleSelectFieldType(stepIndex, type)}
                disabledTypes={disabledTypes}
              />
            ) : null}

            {step.fields.length > 0 ? (
              <FieldsList>
                {step.fields.map((field, fieldIndex) => (
                  <div key={`${field.id}-${fieldIndex}`}>
                    <FieldRow>
                      <TypeBadge>
                        {FIELD_TYPE_OPTIONS.find((item) => item.value === field.type)?.label || field.type}
                      </TypeBadge>
                      <FieldText>
                        <FieldName>{field.label || "Campo sem label"}</FieldName>
                        <FieldMeta>
                          {field.required ? "Obrigatório" : "Opcional"}
                        </FieldMeta>
                      </FieldText>
                      <Button secondary onClick={() => setActiveEditor({ stepIndex, fieldIndex })}>
                        Editar
                      </Button>
                      <Button secondary onClick={() => handleDeleteField(stepIndex, fieldIndex)}>
                        Excluir
                      </Button>
                    </FieldRow>

                    {activeEditor?.stepIndex === stepIndex && activeEditor?.fieldIndex === fieldIndex ? (
                      <FormFieldEditor
                        field={field}
                        onChange={(nextField) => handleFieldChange(stepIndex, fieldIndex, nextField)}
                        onClose={() => setActiveEditor(null)}
                      />
                    ) : null}
                  </div>
                ))}
              </FieldsList>
            ) : (
              <EmptyStepText>Adicione campos para começar esta etapa.</EmptyStepText>
            )}
          </FormStepEditor>
        );
      })}

      {formValues.steps.length === 0 ? (
        <EmptyStepText>Crie a primeira etapa para começar o construtor.</EmptyStepText>
      ) : null}
    </>
  );
}

export default FormBuilderStep;
